import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { CountryCode } from '@delmonicos/shared-design-system-react/src/components/country-code/country-code';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { useGatewayCreateCustomerFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/create-customer.form-validation';
import { GatewayCreateCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/create-customer.model';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const CustomerCreateFormStep1InformationFragment =
  (): React.JSX.Element => {
    const { t } = useTranslation();
    const {
      name,
      holderCompanyName,
      holderSiret,
      holderVatNumber,
      holderAddressAddress,
      holderAddressZipCode,
      holderAddressCity,
      holderAddressCountryCode,
      defaultNetworkName,
    } = useGatewayCreateCustomerFormValidation();
    const { control } = useFormContext<GatewayCreateCustomerModel>();

    return (
      <Card gap={GapEnum.L} direction="column" flex={1}>
        <AccentTitle
          icon="info-circle"
          text={t('$.customer-details.information.title')}
        />
        <Controller
          name="name"
          rules={name}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-customer.name')}
              placeholder={t('delmonicos-shared-sdk.outputs-customer.name')}
              required={!!name.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Divider />

        <Controller
          name="holder.companyName"
          rules={holderCompanyName}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.company-name')}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-holder.company-name',
              )}
              required={!!holderCompanyName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Controller
          name="holder.siret"
          rules={holderSiret}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.siret')}
              placeholder={t('delmonicos-shared-sdk.outputs-holder.siret')}
              required={!!holderSiret.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Controller
          name="holder.vatNumber"
          rules={holderVatNumber}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.vat-number')}
              placeholder={t('delmonicos-shared-sdk.outputs-holder.vat-number')}
              required={!!holderVatNumber.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Divider />
        <Controller
          name="holder.address.address"
          rules={holderAddressAddress}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-address.address')}
              placeholder={t('delmonicos-shared-sdk.outputs-address.address')}
              required={!!holderAddressAddress.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Stack gap={GapEnum.S} justifyContent="space-between" flexWrap="wrap">
          <Controller
            name="holder.address.zipCode"
            rules={holderAddressZipCode}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-address.zip-code')}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-address.zip-code',
                )}
                required={!!holderAddressZipCode.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="holder.address.city"
            rules={holderAddressCity}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-address.city')}
                placeholder={t('delmonicos-shared-sdk.outputs-address.city')}
                required={!!holderAddressCity.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="holder.address.countryCode"
            control={control}
            rules={holderAddressCountryCode}
            render={({ field, fieldState: { error } }) => (
              <CountryCode
                errorMessage={error?.message}
                fieldValue={field.value}
                fieldOnChange={field.onChange}
              />
            )}
          />
        </Stack>
        <Divider />

        <Controller
          name="defaultNetworkName"
          rules={defaultNetworkName}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t(
                'delmonicos-shared-sdk.inputs-create-customer.default-network-name',
              )}
              placeholder={t(
                'delmonicos-shared-sdk.inputs-create-customer.default-network-name',
              )}
              required={!!defaultNetworkName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
      </Card>
    );
  };

export const CustomerCreateFormStep1HolderFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { holderFirstName, holderLastName, holderEmail } =
    useGatewayCreateCustomerFormValidation();
  const { control } = useFormContext<GatewayCreateCustomerModel>();

  return (
    <Card gap={GapEnum.M} flex={1}>
      <AccentTitle
        icon="user-admin"
        variant={AccentVariantEnum.Secondary}
        text={t('delmonicos-shared-sdk.outputs-customer.holder')}
      />

      <Stack gap={GapEnum.M} flexWrap="wrap">
        <Controller
          name="holder.firstName"
          rules={holderFirstName}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.first-name')}
              placeholder={t('delmonicos-shared-sdk.outputs-holder.first-name')}
              required={!!holderFirstName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Controller
          name="holder.lastName"
          rules={holderLastName}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.last-name')}
              placeholder={t('delmonicos-shared-sdk.outputs-holder.last-name')}
              required={!!holderLastName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
      </Stack>

      <Controller
        name="holder.email"
        rules={holderEmail}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-holder.email')}
            placeholder={t('delmonicos-shared-sdk.outputs-holder.email')}
            required={!!holderEmail.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />
    </Card>
  );
};

export const CustomerCreateFormStep1DefaultUserFragment =
  (): React.ReactElement => {
    const { t } = useTranslation();
    const {
      defaultUserDefaultRoleName,
      defaultUserFirstName,
      defaultUserLastName,
      defaultUserEmailAddress,
    } = useGatewayCreateCustomerFormValidation();
    const { control } = useFormContext<GatewayCreateCustomerModel>();

    return (
      <Card direction="column" gap={GapEnum.M} flex={1}>
        <Stack gap={GapEnum.M} justifyContent="space-between">
          <AccentTitle
            icon="user-01"
            variant={AccentVariantEnum.Accent1}
            text={t('$.customer-create.default-user')}
          />
          <Controller
            name="defaultUser.defaultRoleName"
            rules={defaultUserDefaultRoleName}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-user.role')}
                placeholder={t('delmonicos-shared-sdk.outputs-user.role')}
                required={!!defaultUserDefaultRoleName.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
        </Stack>
        <Stack gap={GapEnum.M} flexWrap="wrap">
          <Controller
            name="defaultUser.firstName"
            rules={defaultUserFirstName}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-user.first-name')}
                placeholder={t('delmonicos-shared-sdk.outputs-user.first-name')}
                required={!!defaultUserFirstName.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="defaultUser.lastName"
            rules={defaultUserLastName}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-user.last-name')}
                placeholder={t('delmonicos-shared-sdk.outputs-user.last-name')}
                required={!!defaultUserLastName.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
        </Stack>
        <Controller
          name="defaultUser.emailAddress"
          rules={defaultUserEmailAddress}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.email')}
              placeholder={t('delmonicos-shared-sdk.outputs-holder.email')}
              required={!!defaultUserEmailAddress.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
      </Card>
    );
  };

export const CustomerCreateFormStep1Controller = (): React.JSX.Element => {
  return (
    <Stack gap={GapEnum.L} flex={1}>
      <Stack flex={1}>
        <CustomerCreateFormStep1InformationFragment />
      </Stack>

      <Stack flex={1} direction="column" gap={GapEnum.L}>
        <Stack flex={1}>
          <CustomerCreateFormStep1HolderFragment />
        </Stack>
        <CustomerCreateFormStep1DefaultUserFragment />
      </Stack>
    </Stack>
  );
};
